import { useMemo, useState, type ReactNode } from 'react';
import {
  ApiError,
  addTeamMember,
  createTeam,
  fetchMembers,
  fetchTeams,
  removeTeamMember,
  renameTeam,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { namaLayak } from '../lib/display-name';
import { hasAnyPermission } from '../lib/navigation';
import {
  buildTeamViews,
  kandidatAnggota,
  namaTimProblem,
  NAMA_TIM_MAKS,
  type RosterEntry,
} from '../lib/team-roster';
import { ErrorCard, LoadingState } from '../states';

/**
 * Tim — penentu apa yang DILIHAT seorang Manajer.
 *
 * ADA KARENA cakupan `MANAGER` adalah `team`, dan penyelesaiannya berbunyi "dirinya + anggota tim
 * yang ia kelola". Tanpa tim, Manajer hanya melihat dirinya sendiri: papan absensinya kosong dari
 * kurir mana pun, dan perannya praktis sesempit kurir.
 *
 * Endpoint-endpoint tim sudah ada sejak lama. Yang tidak pernah ada adalah LAYARNYA — sehingga
 * satu-satunya cara membuat tim adalah menulis ke basis data langsung. Ditemukan 23 Agustus 2026
 * pada produksi: NOL tim, sementara dua orang sudah berperan Manajer.
 *
 * Layar ini dibuka pengatur, bukan kurir di jalan, jadi ia boleh berupa daftar dan formulir biasa
 * — tanpa kartu aksen, tanpa angka besar.
 */
export function TeamsPage(): ReactNode {
  const session = useSession();
  const bolehBuat = hasAnyPermission(session.me.permissions, ['workforce.team.create']);
  const bolehUbah = hasAnyPermission(session.me.permissions, ['workforce.team.update']);

  const { state, reload } = useAsync(async () => {
    const [teams, members] = await Promise.all([
      fetchTeams(session.auth),
      fetchMembers(session.auth),
    ]);
    return { teams, members };
  }, [session.auth]);

  const [galat, setGalat] = useState<ApiError | null>(null);
  const [sibuk, setSibuk] = useState(false);

  async function jalankan(aksi: () => Promise<unknown>): Promise<void> {
    setSibuk(true);
    setGalat(null);
    try {
      await aksi();
      reload();
    } catch (e) {
      setGalat(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', 'Gagal.', null));
    } finally {
      setSibuk(false);
    }
  }

  return (
    <>
      <section className="card">
        <h2>Tim</h2>
        <p className="muted small">
          Manajer melihat absensi dan tugas anggota timnya. Tanpa tim, ia hanya melihat dirinya
          sendiri.
        </p>
      </section>

      {galat ? <ErrorCard error={galat} /> : null}
      {state.status === 'loading' ? <LoadingState label="Memuat tim…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        <IsiTim
          teams={state.data.teams}
          members={state.data.members}
          bolehBuat={bolehBuat}
          bolehUbah={bolehUbah}
          sibuk={sibuk}
          onBuat={(nama, manajer) =>
            void jalankan(() => createTeam(session.auth, { name: nama, managerUserId: manajer }))
          }
          onTambah={(teamId, userId) =>
            void jalankan(() => addTeamMember(session.auth, teamId, userId))
          }
          onKeluarkan={(teamId, userId) =>
            void jalankan(() => removeTeamMember(session.auth, teamId, userId))
          }
          onGantiNama={(teamId, nama) =>
            void jalankan(() => renameTeam(session.auth, teamId, nama))
          }
        />
      ) : null}
    </>
  );
}

function IsiTim({
  teams,
  members,
  bolehBuat,
  bolehUbah,
  sibuk,
  onBuat,
  onTambah,
  onKeluarkan,
  onGantiNama,
}: {
  readonly teams: readonly {
    readonly id: string;
    readonly name: string;
    readonly managerUserId: string;
    readonly memberUserIds: readonly string[];
  }[];
  readonly members: readonly { readonly userId: string; readonly displayName: string }[];
  readonly bolehBuat: boolean;
  readonly bolehUbah: boolean;
  readonly sibuk: boolean;
  readonly onBuat: (nama: string, manajerUserId: string) => void;
  readonly onTambah: (teamId: string, userId: string) => void;
  readonly onKeluarkan: (teamId: string, userId: string) => void;
  readonly onGantiNama: (teamId: string, nama: string) => void;
}): ReactNode {
  /*
   * `namaLayak() ?? 'Lengkapi profil'` — SAMA dengan `WorkerManagePage.tsx` dan `rekap-bulanan.ts`
   * (WKF-001, #1150). Sebelum ini `m.displayName` dipakai APA ADANYA: anggota yang displayName-nya
   * belum layak (email mentah, dari bug historis HBS yang pernah mengisi kolom itu dengan email)
   * tampil sebagai alamat emailnya sendiri di halaman ini — satu-satunya layar yang terlewat saat
   * fallback ini dipasang di tempat lain.
   */
  const roster: readonly RosterEntry[] = useMemo(
    () =>
      members.map((m) => ({
        userId: m.userId,
        name: namaLayak(m.displayName) ?? 'Lengkapi profil',
      })),
    [members],
  );
  const daftar = useMemo(() => buildTeamViews(teams, roster), [teams, roster]);

  const [namaBaru, setNamaBaru] = useState('');
  const [manajerBaru, setManajerBaru] = useState('');

  return (
    <>
      {bolehBuat ? (
        <section className="card">
          <h3>Buat tim</h3>
          <label htmlFor="namaTim">Nama tim</label>
          <input
            id="namaTim"
            value={namaBaru}
            onChange={(e) => setNamaBaru(e.target.value)}
            maxLength={120}
            placeholder="mis. Tim Kurir Jakarta"
          />
          <label htmlFor="manajerTim">Manajer</label>
          <select
            id="manajerTim"
            value={manajerBaru}
            onChange={(e) => setManajerBaru(e.target.value)}
          >
            <option value="">Pilih orang…</option>
            {roster.map((r) => (
              <option key={r.userId} value={r.userId}>
                {r.name}
              </option>
            ))}
          </select>
          {/*
            Daftar manajer TIDAK disaring menurut peran. Peran dan tim ditetapkan di dua layar
            berbeda, dan urutannya bebas: menyaring di sini akan menyembunyikan orang yang baru
            saja hendak dijadikan manajer. Server tetap yang memutuskan sahnya.
          */}
          <p className="muted small">
            Orang yang dipilih tidak otomatis menjadi Manajer — peran diatur di layar Karyawan.
          </p>
          <button
            type="button"
            className="button-primary"
            disabled={sibuk || namaBaru.trim().length < 2 || manajerBaru === ''}
            onClick={() => {
              onBuat(namaBaru.trim(), manajerBaru);
              setNamaBaru('');
              setManajerBaru('');
            }}
          >
            {sibuk ? 'Menyimpan…' : 'Buat tim'}
          </button>
        </section>
      ) : null}

      {daftar.length === 0 ? (
        <section className="card">
          <p className="muted small">
            Belum ada tim. Selama belum ada, setiap Manajer hanya melihat absensi dan tugasnya
            sendiri.
          </p>
        </section>
      ) : (
        daftar.map((tim) => (
          <section key={tim.id} className="card">
            {bolehUbah ? (
              <UbahNama tim={tim} sibuk={sibuk} onSimpan={onGantiNama} />
            ) : (
              <h3>{tim.name}</h3>
            )}
            <p className="muted small">Manajer: {tim.managerName}</p>

            {tim.members.length === 0 ? (
              <p className="muted small">Belum ada anggota.</p>
            ) : (
              <ul className="list">
                {tim.members.map((m) => (
                  <li key={m.userId} className="list-item">
                    <div className="row space-between">
                      <span>{m.name}</span>
                      {bolehUbah ? (
                        <button
                          type="button"
                          className="link"
                          disabled={sibuk}
                          onClick={() => onKeluarkan(tim.id, m.userId)}
                        >
                          Keluarkan
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {bolehUbah ? (
              <TambahAnggota tim={tim} roster={roster} sibuk={sibuk} onTambah={onTambah} />
            ) : null}
          </section>
        ))
      )}
    </>
  );
}

function TambahAnggota({
  tim,
  roster,
  sibuk,
  onTambah,
}: {
  readonly tim: ReturnType<typeof buildTeamViews>[number];
  readonly roster: readonly RosterEntry[];
  readonly sibuk: boolean;
  readonly onTambah: (teamId: string, userId: string) => void;
}): ReactNode {
  const [pilihan, setPilihan] = useState('');
  const kandidat = useMemo(() => kandidatAnggota(tim, roster), [tim, roster]);

  if (kandidat.length === 0) {
    return <p className="muted small">Semua orang sudah menjadi anggota tim ini.</p>;
  }

  return (
    <>
      <label htmlFor={`tambah-${tim.id}`}>Tambah anggota</label>
      <select id={`tambah-${tim.id}`} value={pilihan} onChange={(e) => setPilihan(e.target.value)}>
        <option value="">Pilih orang…</option>
        {kandidat.map((r) => (
          <option key={r.userId} value={r.userId}>
            {r.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={sibuk || pilihan === ''}
        onClick={() => {
          onTambah(tim.id, pilihan);
          setPilihan('');
        }}
      >
        Tambahkan
      </button>
    </>
  );
}

/**
 * Ubah nama tim — tersembunyi sampai diminta.
 *
 * ADA KARENA sebelum 23 Agustus 2026 nama tim tidak dapat diperbaiki sama sekali: API hanya punya
 * buat, tambah-anggota, dan keluarkan-anggota. Satu kali salah ketik menetap selamanya kecuali
 * seseorang menulis ke basis data produksi.
 *
 * Formulirnya TIDAK selalu terbuka. Nama tim jarang berubah, dan kotak teks yang selalu tampil di
 * atas tiap kartu membuat layar ini terbaca seperti formulir, bukan seperti daftar — padahal yang
 * dicari orang hampir selalu "siapa anggota tim ini".
 */
function UbahNama({
  tim,
  sibuk,
  onSimpan,
}: {
  readonly tim: ReturnType<typeof buildTeamViews>[number];
  readonly sibuk: boolean;
  readonly onSimpan: (teamId: string, nama: string) => void;
}): ReactNode {
  const [buka, setBuka] = useState(false);
  const [nama, setNama] = useState(tim.name);
  const masalah = namaTimProblem(nama);

  if (!buka) {
    return (
      <div className="row space-between">
        <h3>{tim.name}</h3>
        <button
          type="button"
          className="link"
          onClick={() => {
            setNama(tim.name);
            setBuka(true);
          }}
        >
          Ubah nama
        </button>
      </div>
    );
  }

  return (
    <>
      <label htmlFor={`nama-${tim.id}`}>Nama tim</label>
      <input
        id={`nama-${tim.id}`}
        value={nama}
        onChange={(e) => setNama(e.target.value)}
        maxLength={NAMA_TIM_MAKS}
      />
      {/* Masalahnya disebut SEBELUM tombol ditekan, bukan sesudah server menolaknya. */}
      {masalah ? <p className="warn-text small">{masalah}</p> : null}
      <div className="row">
        <button
          type="button"
          className="button-primary"
          disabled={sibuk || masalah !== null}
          onClick={() => {
            onSimpan(tim.id, nama.trim());
            setBuka(false);
          }}
        >
          {sibuk ? 'Menyimpan…' : 'Simpan nama'}
        </button>
        <button type="button" className="link" onClick={() => setBuka(false)}>
          Batal
        </button>
      </div>
    </>
  );
}
